import { useEffect, useState } from 'react';
import { Plus, AlertCircle, CheckCircle } from 'lucide-react';

const API = import.meta.env.VITE_API_URL;

export default function AffiliateAdjustments() {
  const [adjustments, setAdjustments] = useState<any[]>([]);
  const [affiliates, setAffiliates] = useState<any[]>([]);
  const [periods, setPeriods] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filter, setFilter] = useState({ affiliate_id: '', type: '' });
  const [form, setForm] = useState({ period_id: '', amount: 0, adjustment_type: 'bonus', reason: '' });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchAdjustments = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filter.affiliate_id) params.set('affiliate_id', filter.affiliate_id);
    if (filter.type) params.set('type', filter.type);
    const res = await fetch(`${API}/admin/affiliates/adjustments?${params}`, { credentials: 'include' });
    const json = (await res.json()) as { success: boolean; data: any[] };
    if (json.success) setAdjustments(json.data || []);
    setLoading(false);
  };

  const fetchAffiliates = async () => {
    const res = await fetch(`${API}/admin/affiliates/picker`, { credentials: 'include' });
    const json = (await res.json()) as { success: boolean; data: any[] };
    if (json.success) setAffiliates(json.data || []);
  };

  const fetchPeriodsForAffiliate = async (affiliateId: string) => {
    if (!affiliateId) {
      setPeriods([]);
      return;
    }
    const res = await fetch(`${API}/admin/affiliates/${affiliateId}`, { credentials: 'include' });
    const json = (await res.json()) as { success: boolean; data: any };
    if (json.success) setPeriods(json.data.periods || []);
  };

  useEffect(() => {
    fetchAdjustments();
    fetchAffiliates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const submit = async () => {
    setError(null);
    if (!form.period_id || !form.adjustment_type || !form.reason) {
      setError('Period, type, and reason required');
      return;
    }
    const res = await fetch(`${API}/admin/affiliates/adjustments`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, amount: Number(form.amount) }),
    });
    const json = (await res.json()) as { success: boolean; error?: string };
    if (!json.success) {
      setError(json.error || 'Failed');
      return;
    }
    setSuccess('Adjustment posted.');
    setTimeout(() => setSuccess(null), 3000);
    setShowAdd(false);
    setForm({ period_id: '', amount: 0, adjustment_type: 'bonus', reason: '' });
    fetchAdjustments();
  };

  const affNameById = new Map(affiliates.map((a) => [a.id, a.display_name]));

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Adjustments</h2>
        <button onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          <Plus className="w-4 h-4" /> {showAdd ? 'Cancel' : 'New Adjustment'}
        </button>
      </div>

      {success && <div className="bg-green-50 text-green-700 p-3 rounded mb-4 flex items-center gap-2"><CheckCircle className="w-4 h-4" />{success}</div>}

      {showAdd && (
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <h3 className="font-medium mb-3">New Manual Adjustment</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs text-gray-600">Affiliate</label>
              <select
                onChange={(e) => fetchPeriodsForAffiliate(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm"
              >
                <option value="">Select affiliate...</option>
                {affiliates.map((a) => (
                  <option key={a.id} value={a.id}>{a.display_name} ({a.referral_code})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-600">Period</label>
              <select value={form.period_id} onChange={(e) => setForm({ ...form, period_id: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="">Select period...</option>
                {periods.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.period_start} (status: {p.status}, due: ${Number(p.total_due).toFixed(2)})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-600">Type</label>
              <select value={form.adjustment_type} onChange={(e) => setForm({ ...form, adjustment_type: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="bonus">Bonus</option>
                <option value="correction">Correction</option>
                <option value="dispute_resolution">Dispute Resolution</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-600">Amount (negative for debit)</label>
              <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) })} className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-gray-600">Reason (required)</label>
              <input type="text" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
          </div>
          {error && <div className="text-red-600 text-sm mb-2 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{error}</div>}
          <button onClick={submit} className="px-4 py-1.5 bg-blue-600 text-white rounded text-sm">Post Adjustment</button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-3 mb-4 flex gap-3 flex-wrap">
        <select value={filter.affiliate_id} onChange={(e) => setFilter({ ...filter, affiliate_id: e.target.value })} className="px-3 py-1.5 border rounded text-sm">
          <option value="">All affiliates</option>
          {affiliates.map((a) => (
            <option key={a.id} value={a.id}>{a.display_name}</option>
          ))}
        </select>
        <select value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })} className="px-3 py-1.5 border rounded text-sm">
          <option value="">All types</option>
          <option value="retroactive_credit">Retroactive Credit</option>
          <option value="retroactive_debit">Retroactive Debit</option>
          <option value="bonus">Bonus</option>
          <option value="correction">Correction</option>
          <option value="dispute_resolution">Dispute Resolution</option>
          <option value="termination_writeoff">Termination Writeoff</option>
        </select>
      </div>

      {/* List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : adjustments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No adjustments.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Affiliate</th>
                <th className="text-left p-3">Type</th>
                <th className="text-right p-3">Amount</th>
                <th className="text-left p-3">Reason</th>
              </tr>
            </thead>
            <tbody>
              {adjustments.map((a) => (
                <tr key={a.id} className="border-b">
                  <td className="p-3 text-xs">{new Date(a.created_at).toLocaleDateString()}</td>
                  <td className="p-3">{affNameById.get(a.affiliate_id) || a.affiliate_id}</td>
                  <td className="p-3 text-xs">{a.adjustment_type}</td>
                  <td className={`p-3 text-right font-mono font-bold ${Number(a.amount) < 0 ? 'text-red-600' : 'text-green-700'}`}>
                    ${Number(a.amount).toFixed(2)}
                  </td>
                  <td className="p-3 text-xs text-gray-700">{a.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
